import { LitElement, html } from 'lit';
import { map } from 'lit/directives/map.js';
import { when } from 'lit/directives/when.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { customElement, property, state } from 'lit/decorators.js';
import { commonStyles } from '@styles/common';
import { clearButton } from '@styles/clearButton';
import { inputMaterial } from '@styles/inputMaterial';
import { labelMaterial } from '@styles/labelMaterial';
import { suggestionList } from '@styles/suggestionList';
import { getLocationsList, HotKeys } from '../config';

@customElement('location-typeahead-component')
export class LocationTypeahead extends LitElement {
  static styles = [
    commonStyles,
    clearButton,
    suggestionList,
    inputMaterial,
    labelMaterial,
  ]

  @property()
  isLocationLabelEnabled?: boolean = true;

  @property()
  clearButtonEnabled?: boolean = true;

  @property()
  labelText?: string;

  @property()
  fieldName?: string = 'where';

  @property()
  disabled?: boolean;

  @property()
  placeholder?: string;

  @property()
  locationsLimit = 3;

  @property()
  currentValue = "";

  @state()
  suggestedValue = "";

  @state()
  locationsList: IAutocompleteLocationProvider[] = []

  @state()
  activeItemIndex = 0;

  @state()
  isMouseDown = false;

  render() {
    return html`
      ${when(this.isLocationLabelEnabled, () => html`
      <label
      class="proposal-form__label"
      >
      ${this.labelText}
      </label>
    `)}
      <input 
        type="text" 
        name=${this.fieldName} 
        class="jr-input-material ${this.disabled ? 'jr-input-material--disabled' : ''}" 
        placeholder=${this.placeholder} 
        tabindex="-1" 
        autocomplete="off"
        ?disabled=${ifDefined(this.disabled)}
        .value=${this.currentValue}
        @blur=${this.blurHandler}
        @input=${this.inputHandler}
        @keydown=${this.keyDownHandler} />
      ${when(this.clearButtonEnabled, () => html`
      <button
      type="button"
      class="clear-button"
      @click=${this.clickHandler}
      />`)}
      <label
        class="proposal-form__label"
      >
      ${this.labelText}
      </label>
      <input 
      type="text" 
      class="jr-typeahead-input" 
      tabindex="-1" 
      autocomplete="off"
      readonly="true"
      .value=${this.suggestedValue} />
      ${when(this.locationsList.length, () => html`
        <ul role="listbox" aria-hidden="false" class="dropdown-menu ts-dropdown-menu" @mousedown=${this.suggestionClickHandler}>
        ${map(this.locationsList.slice(0, this.locationsLimit), (location, index) => html`
          <li 
          tabindex="-1" 
          role="option"
          class=${ifDefined(this.activeItemIndex === index ? 'active' : null)}
          @mouseenter=${this.suggestionMouseEnterHandler}
          >
            <a tabindex="-1">${location.canonicalName}</a>
          </li>
        `)}
        </ul>
      `)}
    `
  }

  private async inputHandler(event: Event) {
    const { value } = (<HTMLInputElement>event.target);
    this.currentValue = value;

    this.dispatchLocationChangeEvent();

    if (this.currentValue) {
      this.locationsList =
        await getLocationsList(this.currentValue, this.locationsLimit);
      if (this.locationsList.length) {
        this.suggestedValue = this.locationsList[0].canonicalName;
      } else {
        this.resetSuggestionStates();
      }
    } else {
      this.resetStates();
    }
  }

  private blurHandler() {
    if (!this.isMouseDown) {
      this.resetSuggestionStates();
    }
    this.isMouseDown = false;
  }

  private keyDownHandler(event: KeyboardEvent) {
    const { key } = event;
    if (Object.values(HotKeys).includes(key as HotKeys) && this.locationsList.length) {
      event.preventDefault();
      event.stopPropagation();

      switch (key) {
        case HotKeys.ARROW_DOWN:
          if (this.activeItemIndex < this.locationsList.length - 1) {
            this.activeItemIndex += 1;
            this.suggestedValue = this.locationsList[this.activeItemIndex].canonicalName;
          }

          break;

        case HotKeys.ARROW_UP:
          if (this.activeItemIndex > 0) {
            this.activeItemIndex -= 1;
            this.suggestedValue = this.locationsList[this.activeItemIndex].canonicalName;
          }

          break;

        case HotKeys.ENTER:
        case HotKeys.TAB:
          this.currentValue = this.locationsList[this.activeItemIndex].canonicalName;

          this.resetSuggestionStates();
          this.dispatchLocationChangeEvent();

          break;

        case HotKeys.ESCAPE:
          this.resetSuggestionStates();

          break;

        default:
          break;
      }
    }
  }

  private clickHandler() {
    this.resetStates();

    this.dispatchLocationChangeEvent();
  }

  private suggestionClickHandler(event: MouseEvent) {
    this.isMouseDown = true;
    this.currentValue = (<HTMLElement>event.target).innerText;

    this.resetSuggestionStates();
    this.dispatchLocationChangeEvent();
  }

  private suggestionMouseEnterHandler(event: MouseEvent) {
    this.activeItemIndex = this.locationsList.map(({ canonicalName }) =>
      canonicalName).indexOf((<HTMLElement>event.target).innerText);

    this.suggestedValue = this.locationsList[this.activeItemIndex].canonicalName;
  }

  private dispatchLocationChangeEvent() {
    const event = new CustomEvent('location-change-event', {
      detail: {
        value: this.currentValue
      }
    });

    this.dispatchEvent(event);
  }

  private resetStates() {
    this.currentValue = "";
    this.suggestedValue = "";
    this.locationsList = [];
  }

  private resetSuggestionStates() {
    this.locationsList = [];
    this.suggestedValue = "";
    this.activeItemIndex = 0;
  }
}